import { signOut } from "@/lib/auth-client";
import { queryKeys } from "@/lib/query-keys";
import { authViewPaths, nonAuthViews, type AuthViewPath } from "@/types";
import { AuthView, OrganizationSwitcher, UserInvitationsCard } from "@daveyplate/better-auth-ui";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import z from "zod";

export const Route = createFileRoute("/auth/$authView")({
  component: AuthPages,
  params: {
    parse: ({ authView }) => ({
      authView: z
        .enum(Object.values(authViewPaths) as [AuthViewPath, ...AuthViewPath[]])
        .parse(authView),
    }),
  },
  validateSearch: z.object({
    redirectTo: z.string().optional(),
  }),
  beforeLoad: async ({
    context: { queryClient, authOptions },
    search: { redirectTo },
    params: { authView },
  }) => {
    const session = await queryClient.fetchQuery(authOptions.session);
    if (authView === "organization") {
      if (!session) {
        throw redirect({
          to: "/auth/$authView",
          params: { authView: "sign-in" },
          search: { redirectTo: "/auth/organization" },
        });
      }

      if (session.session.activeOrganizationId) {
        throw redirect({
          to: redirectTo || "/",
        });
      }
    } else {
      if (session && nonAuthViews.includes(authView)) {
        throw redirect({
          to: redirectTo || "/",
        });
      }
    }
  },
});

function AuthPages() {
  const { useParams, useRouteContext } = Route;
  const { authView } = useParams();
  const router = useRouter();
  const { queryClient } = useRouteContext();

  return (
    <main className="min-h-screen">
      <div className="container relative mx-auto min-h-screen px-4 py-6 pt-10 md:pt-32">
        <div className="flex grow flex-col items-center justify-center">
          {authView === "organization" ? (
            <Card className="w-full max-w-xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold">Continue with an Organization</CardTitle>
                <CardDescription>
                  If you are not a member yet, you can accept an invitation or create a new
                  organization below.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                <OrganizationSwitcher
                  onSetActive={() => {
                    queryClient.invalidateQueries({ queryKey: queryKeys.session() });
                    queryClient.invalidateQueries({ queryKey: queryKeys.organizations() });
                    router.invalidate();
                  }}
                  hidePersonal
                  classNames={{
                    trigger: {
                      base: "bg-card border-border border text-card-foreground hover:bg-accent",
                    },
                  }}
                />
                <UserInvitationsCard />
              </CardContent>
              <CardFooter className="flex items-center justify-center">
                <p className="text-muted-foreground text-sm">Want to switch accounts?</p>
                <Button
                  variant={"link"}
                  onClick={() =>
                    signOut().finally(() => {
                      queryClient.invalidateQueries({ queryKey: queryKeys.session() });
                      router.invalidate();
                    })
                  }
                >
                  Sign out
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <AuthView
              pathname={authView}
              classNames={{
                form: {
                  qrCode: "border-white",
                },
              }}
            />
          )}
        </div>
      </div>
    </main>
  );
}
